import { StacklineTheme, useStacklineTheme } from '@stackline/ui';
import React, { useCallback } from 'react';
import { UnstyledButton } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/PromotionMetricCards';
import {
  RatingsAndReviewQueryParams,
  keyMetricsQuality,
  keyMetricsRatings,
  keyMetricsReviews,
  keyMetricsSentiments
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewConstants';
import { RatingsAndReviewMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewMetricTypes';
import { useCombineKeyMetricsData } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewSummary/ReviewSummaryKeyMetricsData';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { BEACON_SUBTABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import { useHistory, useMetricFormatter } from 'src/utils/Hooks';
import { getQueryParamValue, updateUrlQueryParams } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const hiddenRatingsMetrics = new Set([
  RatingsAndReviewMetric.WEEKLY_RATING_VOLUME,
  RatingsAndReviewMetric.WEEKLY_RATING
]);

const hiddenReviewsMetrics = new Set([
  RatingsAndReviewMetric.WEEKLY_REVIEW_VOLUME,
  RatingsAndReviewMetric.WEEKLY_REVIEW_RATING
]);

const hiddenQualityMetrics = new Set([RatingsAndReviewMetric.WORD_COUNT, RatingsAndReviewMetric.KEYWORD_POPULARITY]);

const hiddenSentimentMetrics = new Set([
  RatingsAndReviewMetric.WEEKLY_POSITIVE_SENTIMENT,
  RatingsAndReviewMetric.WEEKLY_NEGATIVE_SENTIMENT
]);

const MetricCard = ({
  label,
  theme,
  selected,
  isLoading = true,
  formattedValue,
  onClick
}: {
  label: string;
  theme: StacklineTheme;
  selected: boolean;
  isLoading: boolean;
  formattedValue: string | number;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}) => {
  return (
    <UnstyledButton
      onClick={onClick}
      stacklineTheme={theme}
      selected={selected}
      sx={{
        transform: 'translateX(-12px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '254px',
        height: '80px',
        padding: '12px 12px 8px 12px'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: '20px',
          textAlign: 'left',
          whiteSpace: 'break-spaces'
        }}
      >
        <Text sx={{ height: '16px' }} color="secondary" variant="subtitle3" transform="uppercase">
          {label}
        </Text>
      </div>
      <Flex height="32px" justifyContent="space-between" alignItems="center">
        {isLoading ? <SlSkeleton width="55px" height="34px" /> : <Text variant="body0">{formattedValue}</Text>}
      </Flex>
    </UnstyledButton>
  );
};

const RatingsSummaryKeyMetricsCards = () => {
  const theme = useStacklineTheme();
  const history = useHistory();
  const formatMetric = useMetricFormatter();

  const { keyMetricsCardData } = useCombineKeyMetricsData();

  const handleCardClick = useCallback(
    (subtab: string) => {
      history.push(updateUrlQueryParams({ subtab }));
    },
    [history]
  );

  return (
    <Flex justifyContent="space-between" gap={12}>
      {/* Ratings */}
      <Flex flexDirection="column">
        <Flex marginBottom="12px">
          <Text variant="h4">Ratings</Text>
        </Flex>
        {keyMetricsRatings
          .filter((metric) => !hiddenRatingsMetrics.has(metric.id))
          .map((metric) => (
            <MetricCard
              key={metric.id}
              label={metric.label}
              theme={theme}
              selected={
                getQueryParamValue(RatingsAndReviewQueryParams, RatingsAndReviewMetric.WEEKLY_RATING_VOLUME) ===
                metric.id
              }
              isLoading={!(metric.id in keyMetricsCardData)}
              formattedValue={
                keyMetricsCardData
                  ? formatMetric(
                      keyMetricsCardData[metric.id],
                      metric.metricType ? metric.metricType : METRICTYPE.VOLUME,
                      {
                        decimalPlaces: 1,
                        showFullValue: metric?.metricType !== METRICTYPE.VOLUME,
                        showNegative: true
                      }
                    )
                  : '0'
              }
              onClick={() => handleCardClick(BEACON_SUBTABS.REVIEW_RATINGS)}
            />
          ))}
      </Flex>

      {/* Reviews */}
      <Flex flexDirection="column">
        <Flex marginBottom="12px">
          <Text variant="h4">Reviews</Text>
        </Flex>
        {keyMetricsReviews
          .filter((metric) => !hiddenReviewsMetrics.has(metric.id))
          .map((metric) => (
            <MetricCard
              key={metric.id}
              label={metric.label}
              theme={theme}
              selected={
                getQueryParamValue(RatingsAndReviewQueryParams, RatingsAndReviewMetric.WEEKLY_RATING_VOLUME) ===
                metric.id
              }
              isLoading={!(metric.id in keyMetricsCardData)}
              formattedValue={
                keyMetricsCardData
                  ? formatMetric(
                      keyMetricsCardData[metric.id],
                      metric.metricType ? metric.metricType : METRICTYPE.VOLUME,
                      {
                        decimalPlaces: 1,
                        showFullValue: metric?.metricType !== METRICTYPE.VOLUME,
                        showNegative: true
                      }
                    )
                  : '0'
              }
              onClick={() => handleCardClick(BEACON_SUBTABS.REVIEW_REVIEW)}
            />
          ))}
      </Flex>

      {/* Quality */}
      <Flex flexDirection="column">
        <Flex marginBottom="12px">
          <Text variant="h4">Quality</Text>
        </Flex>
        {keyMetricsQuality
          .filter((metric) => !hiddenQualityMetrics.has(metric.id))
          .map((metric) => (
            <MetricCard
              key={metric.id}
              label={metric.label}
              theme={theme}
              selected={
                getQueryParamValue(RatingsAndReviewQueryParams, RatingsAndReviewMetric.WEEKLY_RATING_VOLUME) ===
                metric.id
              }
              isLoading={!(metric.id in keyMetricsCardData)}
              formattedValue={
                keyMetricsCardData
                  ? formatMetric(keyMetricsCardData[metric.id], METRICTYPE.PERCENT, {
                      decimalPlaces: 1,
                      showFullValue: true,
                      showNegative: true
                    })
                  : '0'
              }
              onClick={() => handleCardClick(BEACON_SUBTABS.REVIEW_REVIEW)}
            />
          ))}
      </Flex>

      {/* Sentiment */}
      <Flex flexDirection="column">
        <Flex marginBottom="12px">
          <Text variant="h4">Sentiment</Text>
        </Flex>
        {keyMetricsSentiments
          .filter((metric) => !hiddenSentimentMetrics.has(metric.id))
          .map((metric) => (
            <MetricCard
              key={metric.id}
              label={metric.label}
              theme={theme}
              selected={
                getQueryParamValue(RatingsAndReviewQueryParams, RatingsAndReviewMetric.WEEKLY_RATING_VOLUME) ===
                metric.id
              }
              isLoading={!(metric.id in keyMetricsCardData)}
              formattedValue={
                keyMetricsCardData
                  ? formatMetric(keyMetricsCardData[metric.id], METRICTYPE.PERCENT, {
                      decimalPlaces: 1,
                      showFullValue: true,
                      showNegative: true
                    })
                  : '0'
              }
              onClick={() => handleCardClick(BEACON_SUBTABS.REVIEW_SENTIMENT)}
            />
          ))}
      </Flex>
    </Flex>
  );
};

export default RatingsSummaryKeyMetricsCards;
